import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    inject,
    input,
    signal,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25LoadingInlineModule } from "../../modules/s25-loading/s25.loading.inline.module";
import { S25RichTextEditorModule } from "../../modules/s25-rich-text-editor/s25.rich.text.editor.module";
import { S25MultiselectModule } from "../../modules/s25-multiselect/s25-multiselect.module";
import { MultiselectModelI } from "../../modules/s25-multiselect/s25.multiselect.component";
import { S25DatepickerModule } from "../../modules/s25-datepicker/s25.datepicker.module";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { S25ItemI } from "../../pojo/S25ItemI";
import {
    NewsArticle,
    NewsArticleRequest,
    NewsArticleService,
    NewsArticleVisibility,
    NewsListView,
} from "../../services/news.article.service";
import { SeOrgSecurityStore } from "../../modules/student-engagement/se.org.security.store";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { S25FilestoreImageComponent } from "../../standalone/s25.filestore.image.component";
import { NewsArticleListComponent } from "./news.article.list.component";

/** Normalize API schedule timestamps for date pickers: start → start of day, end → end of day. */
function scheduleDateForPicker(apiDateTime: string | Date | undefined, bound: "start" | "end"): Date | null {
    if (apiDateTime == null || apiDateTime === "") {
        return null;
    }
    const parsed = typeof apiDateTime === "string" ? S25Util.date.parse(apiDateTime) : apiDateTime;
    if (!parsed || !S25Util.date.isValid(parsed)) {
        return null;
    }
    return bound === "end" ? S25Util.date.toEndOfDay(parsed) : S25Util.date.toStartOfDay(parsed);
}

@TypeManagerDecorator("s25-ng-organization-news")
@Component({
    selector: "s25-ng-organization-news",
    template: `
        @if (articlesResource.resource.isLoading()) {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        } @else {
            <div class="news-header">
                @if (!hideHeading()) {
                    @switch (headingLevel()) {
                        @case (1) {
                            <h1>News</h1>
                        }
                        @case (3) {
                            <h3>News</h3>
                        }
                        @case (4) {
                            <h4>News</h4>
                        }
                        @default {
                            <h2>News</h2>
                        }
                    }
                }
                <div class="news-header__actions">
                    @if (canManage() && !isFormOpen()) {
                        <div class="news-view-filter" role="tablist" aria-label="News article view">
                            <button
                                type="button"
                                class="news-view-filter__option"
                                role="tab"
                                [attr.aria-selected]="listView() === 'active'"
                                [class.news-view-filter__option--selected]="listView() === 'active'"
                                (click)="listView.set('active')"
                            >
                                Active
                            </button>
                            <button
                                type="button"
                                class="news-view-filter__option"
                                role="tab"
                                [attr.aria-selected]="listView() === 'archived'"
                                [class.news-view-filter__option--selected]="listView() === 'archived'"
                                (click)="listView.set('archived')"
                            >
                                Archived
                            </button>
                        </div>
                    }
                    @if (canManage()) {
                        <s25-ng-button type="outline" (clicked)="startCreate()">
                            <s25-ng-icon type="plus" size="0.85em" aria-hidden="true" [ariaLabel]="false" />
                            Create Article
                        </s25-ng-button>
                    }
                </div>
            </div>

            @if (successMessage()) {
                <div class="news-success" role="status" aria-live="polite">{{ successMessage() }}</div>
            }

            @if (isFormOpen()) {
                <div class="news-form">
                    <h3>{{ isEditMode() ? "Edit Article" : "New Article" }}</h3>
                    <div class="form-row">
                        <label class="ngBold" for="news-title">Title</label>
                        <input
                            id="news-title"
                            class="cn-form__control"
                            type="text"
                            [value]="formTitle()"
                            (input)="formTitle.set($any($event.target).value)"
                        />
                    </div>

                    <div class="form-row">
                        <label class="ngBold">Description</label>
                        <s25-ng-rich-text-editor
                            [modelValue]="formDescription()"
                            (modelValueChange)="onDescriptionChange($event)"
                        ></s25-ng-rich-text-editor>
                    </div>

                    <div class="form-row form-row--image">
                        <label class="ngBold">Image</label>
                        <s25-ng-filestore-image
                            [value]="formImageUrl()"
                            (valueChange)="formImageUrl.set($event)"
                            [objectType]="-1"
                            [objectId]="-1"
                            [fileKey]="formArticleId() ? 'news-img-' + formArticleId() : undefined"
                            [saveButton]="false"
                            [autoSaveOnClear]="false"
                        />
                    </div>

                    <div class="form-row">
                        <label class="ngBold" for="news-visibility">Visibility</label>
                        <select
                            id="news-visibility"
                            class="cn-form__control"
                            [value]="formVisibility()"
                            (change)="formVisibility.set($any($event.target).value)"
                        >
                            <option value="PUBLIC">Public</option>
                            <option value="LOGGED_IN">Logged-in users</option>
                            <option value="ORG_MEMBERS">Organization members</option>
                            <option value="PRIVATE">Private (specific roles)</option>
                        </select>
                    </div>

                    @if (formVisibility() === "PRIVATE") {
                        <div class="form-row">
                            <label class="ngBold" for="news-roles">Allowed Roles</label>
                            <s25-ng-multiselect-search-criteria
                                id="news-roles"
                                [type]="'organizationRoles'"
                                [modelBean]="rolesMultiselectModel()"
                                [selectedItems]="formAllowedRoleIds()"
                                [showResults]="true"
                                [useSecurity]="true"
                                (close)="onRolesChange($event)"
                            />
                        </div>
                    }

                    <div class="form-row">
                        <p class="form-hint">
                            Optional publish window. Leave dates blank to publish immediately and indefinitely. The
                            article hides from members after the end date.
                        </p>
                    </div>

                    <div class="form-row">
                        <span class="ngBold">Publish start (optional)</span>
                        <div class="form-date-row">
                            <s25-datepicker
                                [inline]="true"
                                [allowEmpty]="true"
                                [autoSelectToday]="false"
                                [value]="formPublishStartDt()"
                                (valueChange)="formPublishStartDt.set($event)"
                            />
                            <s25-ng-button type="outline" (clicked)="formPublishStartDt.set(null)">Clear</s25-ng-button>
                        </div>
                    </div>

                    <div class="form-row">
                        <span class="ngBold">Publish end (optional)</span>
                        <div class="form-date-row">
                            <s25-datepicker
                                [inline]="true"
                                [allowEmpty]="true"
                                [autoSelectToday]="false"
                                [value]="formPublishEndDt()"
                                (valueChange)="formPublishEndDt.set($event)"
                            />
                            <s25-ng-button type="outline" (clicked)="formPublishEndDt.set(null)">Clear</s25-ng-button>
                        </div>
                    </div>

                    @if (formPublishDateError()) {
                        <div class="form-error" role="alert">{{ formPublishDateError() }}</div>
                    }

                    <div class="form-actions">
                        <s25-ng-button
                            type="primary"
                            [isLoading]="isSaving()"
                            [disabled]="!isFormValid()"
                            (clicked)="saveArticle()"
                        >
                            {{ isEditMode() ? "Update" : "Create" }}
                        </s25-ng-button>
                        <s25-ng-button type="outline" [disabled]="isSaving()" (clicked)="cancelForm()">
                            Cancel
                        </s25-ng-button>
                    </div>
                </div>
            }

            @if (!isFormOpen()) {
                <s25-ng-news-article-list
                    [articles]="articles()"
                    [canManage]="canManage()"
                    [listView]="listView()"
                    (editArticle)="startEdit($event)"
                    (deleteArticle)="confirmDelete($event)"
                />
            }
        }
    `,
    styles: `
        .news-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
            gap: 1rem;
        }

        .news-header__actions {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex-wrap: wrap;
        }

        .news-view-filter {
            display: inline-flex;
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }

        .news-view-filter__option {
            border: none;
            background: var(--color--surface-bg);
            color: var(--color--text);
            padding: 0.4rem 0.85rem;
            cursor: pointer;
            font-size: 0.875rem;
        }

        .news-view-filter__option--selected {
            background: rgba(var(--cn-blue-rgb), 0.12);
            color: var(--color--link-text-hover);
            font-weight: 600;
        }

        .form-hint {
            margin: 0;
            font-size: 0.875rem;
            color: var(--color--text-muted);
        }

        .form-date-row {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex-wrap: wrap;
            margin-top: 0.35rem;
        }

        .news-success {
            padding: 0.75rem 1rem;
            border-radius: 8px;
            background: var(--color--surface-2-bg);
            border-left: 3px solid var(--color--objects-event);
            color: var(--color--text);
            margin-bottom: 1rem;
        }

        .news-form {
            padding: 1.5rem;
            border: 1px solid var(--color--border);
            border-radius: 8px;
            margin-bottom: 2rem;
            background: var(--color--surface-bg);
        }

        .form-row {
            margin-bottom: 1rem;
        }

        .form-row label {
            display: block;
            margin-bottom: 0.35rem;
        }

        .form-row input.error {
            border-color: var(--color--danger);
        }

        .form-error {
            color: var(--color--danger);
            font-size: 0.875rem;
            margin-top: 0.25rem;
        }

        .form-actions {
            display: flex;
            gap: 0.75rem;
            margin-top: 1.5rem;
        }

        .form-row--image ::ng-deep .fs-image__preview {
            max-width: 100%;
            max-height: 300px;
            width: auto;
            height: auto;
            object-fit: contain;
            border-radius: 6px;
        }

        @media (max-width: 768px) {
            .news-header {
                flex-direction: column;
                align-items: stretch;
            }

            .news-header__actions {
                flex-direction: column;
                align-items: stretch;
            }

            .news-view-filter {
                width: 100%;
            }

            .news-view-filter__option {
                flex: 1;
            }

            .news-header h2 {
                margin: 0;
            }

            .news-form {
                padding: 1rem;
            }

            .form-actions {
                flex-direction: column;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        S25LoadingInlineModule,
        S25RichTextEditorModule,
        S25MultiselectModule,
        S25DatepickerModule,
        S25ButtonComponent,
        S25IconComponent,
        S25FilestoreImageComponent,
        NewsArticleListComponent,
    ],
})
export class OrganizationNewsComponent {
    orgId = input.required<number>();
    readonly = input<boolean>(false);
    limit = input<number>(0);
    hideHeading = input<boolean>(false);
    headingLevel = input<number>(2);

    private readonly orgSecStore = inject(SeOrgSecurityStore);
    private readonly cdr = inject(ChangeDetectorRef);

    private readonly filestoreImage = viewChild(S25FilestoreImageComponent);

    articlesResource = s25Resource({
        if: () => !!this.orgId(),
        params: () => ({
            orgId: this.orgId(),
            view: this.listView() === "archived" ? ("archived" as const) : undefined,
        }),
        loader: ({ params }) => NewsArticleService.getNewsArticles(params.orgId, { view: params.view }),
    });

    readonly canManage = computed(() => !this.readonly() && this.orgSecStore.canManageNews());

    listView = signal<NewsListView>("active");

    articles = computed(() => {
        const sorted = (this.articlesResource.value() ?? [])
            .slice()
            .sort((articleA, articleB) =>
                (articleB.publishStartDt ?? articleB.createDt ?? "").localeCompare(
                    articleA.publishStartDt ?? articleA.createDt ?? "",
                ),
            );
        const maxItems = this.limit();
        return maxItems > 0 ? sorted.slice(0, maxItems) : sorted;
    });

    readonly successMessage = signal<string>("");

    readonly successClearEffect = effect((onCleanup) => {
        const message = this.successMessage();
        if (!message) return;
        const timerId = setTimeout(() => this.successMessage.set(""), 4000);
        onCleanup(() => clearTimeout(timerId));
    });

    isFormOpen = signal(false);
    isSaving = signal(false);
    formArticleId = signal<string | undefined>(undefined);
    formTitle = signal("");
    formDescription = signal("");
    private descriptionEditorReady = false;
    formImageUrl = signal("");
    formVisibility = signal<NewsArticleVisibility>("PUBLIC");
    formAllowedRoleIds = signal<S25ItemI[]>([]);
    formPromotedToHome = signal(false);
    formPublishStartDt = signal<Date | null>(null);
    formPublishEndDt = signal<Date | null>(null);

    isEditMode = computed(() => !!this.formArticleId());

    formPublishDateError = computed(() => {
        const startValue = this.formPublishStartDt();
        const endValue = this.formPublishEndDt();
        if (!startValue || !endValue) {
            return "";
        }
        const startDt = S25Util.date.toStartOfDay(startValue);
        const endDt = S25Util.date.toEndOfDay(endValue);
        if (!startDt || !endDt || !S25Util.date.isValid(startDt) || !S25Util.date.isValid(endDt)) {
            return "";
        }
        if (startDt > endDt) {
            return "End date must be on or after start date.";
        }
        return "";
    });

    isFormValid = computed(() => !this.formPublishDateError());

    rolesMultiselectModel = signal<MultiselectModelI>({
        uuid: "news-roles-multiselect",
        title: "Roles",
        hasFilter: true,
        hasSelectAll: true,
        hasSelectNone: true,
        showResult: true,
    });

    async startCreate() {
        if (!this.canManage()) {
            return;
        }
        this.formArticleId.set(undefined);
        this.formTitle.set("");
        this.formDescription.set("");
        this.formImageUrl.set("");
        this.formVisibility.set("PUBLIC");
        this.formAllowedRoleIds.set([]);
        this.formPromotedToHome.set(false);
        this.formPublishStartDt.set(null);
        this.formPublishEndDt.set(null);
        this.descriptionEditorReady = true;
        this.isFormOpen.set(true);
    }

    async startEdit(article: NewsArticle) {
        if (!this.canManage()) {
            return;
        }
        this.formArticleId.set(article.newsArticleId);
        this.formTitle.set(article.title ?? "");
        this.formDescription.set(article.description ?? "");
        this.formImageUrl.set(article.imageUrl ?? "");
        this.formVisibility.set(article.visibility ?? "PUBLIC");
        this.formAllowedRoleIds.set([]);
        this.formPromotedToHome.set(article.promotedToHome ?? false);
        this.formPublishStartDt.set(scheduleDateForPicker(article.publishStartDt, "start"));
        this.formPublishEndDt.set(scheduleDateForPicker(article.publishEndDt, "end"));
        this.descriptionEditorReady = false;
        this.isFormOpen.set(true);

        const editingId = article.newsArticleId;

        // Fetch the full article to get role IDs and promotedToHome
        let roleIds: S25ItemI[] = [];
        let promotedToHome = article.promotedToHome ?? false;
        try {
            if (article.newsArticleId) {
                const fullArticle = await NewsArticleService.getNewsArticle(this.orgId(), article.newsArticleId);
                if (fullArticle && this.formArticleId() === editingId) {
                    promotedToHome = fullArticle.promotedToHome ?? false;
                    this.formPublishStartDt.set(scheduleDateForPicker(fullArticle.publishStartDt, "start"));
                    this.formPublishEndDt.set(scheduleDateForPicker(fullArticle.publishEndDt, "end"));
                    if (fullArticle.roleIds?.length) {
                        roleIds = fullArticle.roleIds
                            .filter((role) => role.contRoleId != null)
                            .map((role) => ({
                                itemId: role.contRoleId!,
                                itemName: "",
                            }));
                    }
                    this.formAllowedRoleIds.set(roleIds);
                    this.formPromotedToHome.set(promotedToHome);
                }
            }
        } catch (error) {
            console.warn("Failed to fetch full article:", error);
            if (article.visibility !== "PRIVATE") {
                this.formAllowedRoleIds.set(roleIds);
                this.formPromotedToHome.set(promotedToHome);
            }
        }
    }

    cancelForm() {
        this.isFormOpen.set(false);
    }

    async saveArticle() {
        if (this.isSaving()) {
            return;
        }

        if (!this.canManage()) {
            return;
        }

        if (!this.isFormValid()) {
            return;
        }

        this.isSaving.set(true);

        try {
            const allowedRoleIds = this.parseAllowedRoles();
            const publishDates = this.buildPublishDatesForSave();
            let articleId = this.formArticleId();

            // If creating, create article first to get the article ID (needed for fileKey)
            if (!this.isEditMode()) {
                const createRequest: NewsArticleRequest = {
                    article: {
                        title: this.formTitle(),
                        description: this.formDescription(),
                        visibility: this.formVisibility(),
                        promotedToHome: false,
                        ...publishDates,
                    },
                    allowedRoleIds: allowedRoleIds.length ? allowedRoleIds : undefined,
                };
                const createdArticle = await NewsArticleService.createNewsArticle(this.orgId(), createRequest);
                articleId = createdArticle.newsArticleId;
                if (!articleId) {
                    throw new Error("Failed to create article - no article ID returned");
                }
                this.formArticleId.set(articleId);
                // Flush the fileKey binding to the child component before calling guardedSave(),
                // so effectiveFileKey() resolves to 'news-img-<articleId>' rather than undefined.
                this.cdr.detectChanges();
            }

            // Upload image (with embedded alt text) if a new file was selected or alt text changed.
            // valueChange updates formImageUrl so the PUT below always carries the latest imageUrl.
            await this.filestoreImage()?.guardedSave();

            // Update article with final image URL, title, description, etc.
            const updateRequest = {
                article: {
                    newsArticleId: articleId,
                    title: this.formTitle(),
                    description: this.formDescription(),
                    imageUrl: this.formImageUrl(),
                    visibility: this.formVisibility(),
                    promotedToHome: this.isEditMode() ? this.formPromotedToHome() : false,
                    ...publishDates,
                },
                allowedRoleIds: allowedRoleIds.length ? allowedRoleIds : undefined,
            };

            await NewsArticleService.updateNewsArticle(this.orgId(), articleId!, updateRequest);

            this.articlesResource.resource.reload();
            this.isFormOpen.set(false);
            this.successMessage.set("Article saved.");
        } catch (caughtError) {
            S25Util.showError(caughtError, "Error saving news article");
        } finally {
            this.isSaving.set(false);
        }
    }

    async confirmDelete(article: NewsArticle) {
        if (!this.canManage()) {
            return;
        }
        if (!article.newsArticleId) {
            return;
        }
        try {
            await NewsArticleService.deleteNewsArticle(this.orgId(), article.newsArticleId);
            this.articlesResource.resource.reload();
            this.successMessage.set("Article deleted.");
        } catch (caughtError) {
            S25Util.showError(caughtError, "Error deleting news article");
        }
    }

    onDescriptionChange(value: string) {
        if (value) {
            this.descriptionEditorReady = true;
        }
        if (!value && !this.descriptionEditorReady) {
            const current = this.formDescription();
            if (current) {
                this.formDescription.set("");
                setTimeout(() => this.formDescription.set(current));
            }
            return;
        }
        this.formDescription.set(value);
    }

    onRolesChange(selectedItems: S25ItemI[]) {
        this.formAllowedRoleIds.set(selectedItems);
    }

    private parseAllowedRoles(): number[] {
        if (this.formVisibility() !== "PRIVATE") {
            return [];
        }
        return this.formAllowedRoleIds()
            .map((item) => item.itemId)
            .filter((id): id is number => typeof id === "number" && Number.isFinite(id));
    }

    private buildPublishDatesForSave(): Pick<NewsArticle, "publishStartDt" | "publishEndDt"> {
        if (this.formPublishDateError()) {
            throw new Error(this.formPublishDateError());
        }

        const startValue = this.formPublishStartDt();
        const endValue = this.formPublishEndDt();
        const publishDates: Pick<NewsArticle, "publishStartDt" | "publishEndDt"> = {};

        const hasStart = startValue != null && S25Util.date.isValid(startValue);
        const hasEnd = endValue != null && S25Util.date.isValid(endValue);

        if (hasStart) {
            const startDt = S25Util.date.toStartOfDay(startValue);
            if (startDt && S25Util.date.isValid(startDt)) {
                publishDates.publishStartDt = S25Util.date.toS25ISODateTimeStr(startDt);
            }
        }
        if (hasEnd) {
            const endDt = S25Util.date.toEndOfDay(endValue);
            if (endDt && S25Util.date.isValid(endDt)) {
                publishDates.publishEndDt = S25Util.date.toS25ISODateTimeStr(endDt);
            }
        }

        return publishDates;
    }
}
